import {
  attributeScopeApplies,
  isContactAttributeType,
  type GeneratedNodeAttribute,
  type GeneratedStatus,
  type InstanceDetail,
  type InstanceSnapshot,
} from "@keydials/api-client";
import { graphemeCount, truncateGraphemes } from "@keydials/dial-renderer";
import type { DialVM, VSection, VSegment } from "../../dialModel";
import { sectionTitle } from "../../dialModel";
import { sectionStatusPalette } from "../../instanceModel";
import { missingGateAttributes } from "../statusGate";
import { resolveContact } from "../contactAttribute";

/**
 * Look back's compare card, the pure half: which rows a level shows, what each says Then and Now, whether
 * the two differ, and what "Bring back" would write. Kept free of React so every rule the card lives by —
 * what counts as a change, what may be copied and why not — is testable without mounting a workspace.
 */

/** Where the card is: the whole dial, one section, or one action point. */
export type CompareLevel =
  | { level: "dial" }
  | { level: "section"; sectionKey: string }
  | { level: "action"; sectionKey: string; segmentKey: string };

/** One side of a row, ready to draw. */
export interface CompareCell {
  text: string;
  /** A status's colour, drawn as a dot beside its name. */
  colour?: string;
  /** Nothing there — drawn as an italic "No value". */
  empty?: boolean;
  /** "15 days ago", on a review date that has passed. */
  overdue?: string;
}

/** What Bring back writes: always an ordinary change made today, to the present. Never to Then. */
export type BringBackWrite =
  | { kind: "segmentStatus"; sectionKey: string; segmentKey: string; statusKey: string }
  | { kind: "reviewDate"; sectionKey: string; segmentKey: string; statusKey: string; reviewDate: string | null; revertToStatusKey: string | null }
  | { kind: "itemStatus"; sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string; statusKey: string }
  | { kind: "sectionStatus"; sectionKey: string; statusKey: string | null }
  | { kind: "field"; target: string; sectionKey: string; segmentKey: string; itemKey: string; attributeKey: string; value: string };

export interface CompareRow {
  id: string;
  label: string;
  then: CompareCell;
  now: CompareCell;
  changed: boolean;
  /** Empty Then and filled Now — the row's chip says "Empty then" rather than "Changed". */
  emptyThen: boolean;
  /** A rollup is worked out from other rows, so there is nothing of its own to copy. */
  rollup?: boolean;
  /** Present when the row can be copied: the write, and the one that would undo it. */
  bringBack?: { write: BringBackWrite; undo: BringBackWrite; describe: string };
  /** Why the row can't be brought back, when it's a change that nonetheless can't be copied. */
  blocked?: string;
}

/** What the card is allowed to do, answered by the SAME rules the live controls use. */
export interface ComparePermissions {
  canSet: (status: GeneratedStatus, sectionKey: string, segmentKey: string) => boolean;
  canEditAttributes: boolean;
  attributeAccess?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
}

/** "3 Oct 2026" — review dates and Date fields. */
function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** "15 days ago", or undefined when the date hasn't passed. */
export function overdueBy(iso: string | null | undefined, now: Date = new Date()): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  return days >= 1 ? `${days} day${days === 1 ? "" : "s"} ago` : undefined;
}

const FILE_TYPES = new Set(["Document", "Image", "Video"]);

/**
 * A field value as one line. The card compares, it doesn't re-render: a rich-text answer becomes its text,
 * a list its entries, a table its row count and files their count, so a row stays one line tall whatever
 * the field holds. The full value is on the node, one click away.
 */
export function summariseValue(type: string | undefined, value: string | null | undefined): CompareCell {
  if (value == null || value.trim() === "") return { text: "No value", empty: true };
  if (type === "Date") return { text: fmtDate(value) ?? value };
  if (type === "RichText") {
    const text = value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    return text ? { text: clip(text) } : { text: "No value", empty: true };
  }
  if (value.startsWith("[") || value.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        if (type === "Table") return { text: `${parsed.length} row${parsed.length === 1 ? "" : "s"}` };
        if (type && FILE_TYPES.has(type)) return { text: `${parsed.length} file${parsed.length === 1 ? "" : "s"}` };
        if (parsed.every((p) => typeof p === "string")) return { text: clip((parsed as string[]).join(", ")) };
        return { text: `${parsed.length} entr${parsed.length === 1 ? "y" : "ies"}` };
      }
      return { text: "Filled in" };
    } catch {
      /* not JSON after all — shown as text */
    }
  }
  if (type && FILE_TYPES.has(type)) return { text: "1 file" };
  return { text: clip(value) };
}

const clip = (s: string) => (graphemeCount(s) > 80 ? `${truncateGraphemes(s, 79)}…` : s);

const statusCell = (s: GeneratedStatus | undefined, emptyText = "No status"): CompareCell =>
  s ? { text: s.name, colour: s.colour } : { text: emptyText, empty: true };

const same = (a: CompareCell, b: CompareCell) => a.text === b.text && !!a.empty === !!b.empty;

function makeRow(id: string, label: string, then: CompareCell, now: CompareCell, extra: Partial<CompareRow> = {}): CompareRow {
  const changed = !same(then, now);
  return { id, label, then, now, changed, emptyThen: changed && !!then.empty && !now.empty, ...extra };
}

/** The declaration a value belongs to, by target and key. */
const declOf = (detail: InstanceDetail, target: string, key: string) =>
  (detail.nodeAttributes ?? []).find((d) => d.target === target && d.key === key);

/** Why a status can't be brought back, or null when it can — the role first, then the status's gate. */
function statusBlock(
  status: GeneratedStatus,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
  detail: InstanceDetail,
  perms: ComparePermissions,
): string | null {
  if (!perms.canSet(status, sectionKey, segmentKey)) {
    return `Your role can't set “${status.name}” here.`;
  }
  const target = itemKey ? "Item" : "Segment";
  const decls = (detail.nodeAttributes ?? []).filter(
    (d) => d.target === target && attributeScopeApplies(d.target, d.appliesToKeys, sectionKey, segmentKey, itemKey),
  );
  const valueOf = (attrKey: string) =>
    (detail.nodeAttributeValues ?? []).find(
      (v) => v.nodeType === target && v.sectionKey === sectionKey && v.segmentKey === segmentKey
        && (v.itemKey ?? "") === itemKey && v.attributeKey === attrKey,
    )?.value ?? "";
  const missing = missingGateAttributes(status, decls, valueOf);
  if (missing.length > 0) {
    return `Can't bring back “${status.name}”: it needs ${missing.map((d) => `“${d.label}”`).join(", ")} filled in first.`;
  }
  return null;
}

/** The field rows for one node: every field that has a value on either side, in declaration order. */
function fieldRows(
  detail: InstanceDetail,
  snapshot: InstanceSnapshot,
  target: string,
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
  perms: ComparePermissions,
  labelPrefix = "",
): CompareRow[] {
  const match = (v: { nodeType: string; sectionKey: string; segmentKey: string; itemKey?: string | null }) =>
    v.nodeType === target
    && (target === "Dial" || v.sectionKey === sectionKey)
    && (target === "Dial" || target === "Section" || v.segmentKey === segmentKey)
    && (target !== "Item" || (v.itemKey ?? "") === itemKey);
  const nowValues = new Map((detail.nodeAttributeValues ?? []).filter(match).map((v) => [v.attributeKey, v.value]));
  const thenValues = new Map((snapshot.fields ?? []).filter(match).map((f) => [f.attributeKey, f.value]));
  const keys = [...new Set([...nowValues.keys(), ...thenValues.keys()])]
    .map((key) => ({ key, decl: declOf(detail, target, key) }))
    // A field whose declaration has gone since is no longer anything the dial asks — nothing to compare.
    .filter((x): x is { key: string; decl: GeneratedNodeAttribute } => !!x.decl)
    .sort((a, b) => (a.decl.order ?? 0) - (b.decl.order ?? 0));

  // KEY-752: a Contact value is an id; the card compares the contact's NAME, as the field itself shows it.
  const shown = (decl: GeneratedNodeAttribute, value: string | null) =>
    value && isContactAttributeType(decl.type)
      ? resolveContact(value, detail.contacts)?.name ?? "A contact no longer on the list"
      : value;
  return keys.map(({ key, decl }) => {
    const thenValue = thenValues.has(key) ? thenValues.get(key)! : nowValues.get(key) ?? null;
    const nowValue = nowValues.get(key) ?? null;
    const row = makeRow(
      `field:${target}:${sectionKey}:${segmentKey}:${itemKey}:${key}`,
      `${labelPrefix}${decl.label}`,
      summariseValue(decl.type, shown(decl, thenValue)),
      summariseValue(decl.type, shown(decl, nowValue)),
    );
    // Compared on the raw value too: two different rich-text answers can summarise to the same line.
    row.changed = row.changed || (thenValue ?? "") !== (nowValue ?? "");
    if (!row.changed) return row;
    const base = { target, sectionKey, segmentKey, itemKey, attributeKey: key };
    if (FILE_TYPES.has(decl.type)) {
      return { ...row, blocked: "Files can't be brought back from here: attach them again from Files." };
    }
    const editable = perms.canEditAttributes && (perms.attributeAccess?.(decl).edit ?? true);
    if (!editable) {
      return { ...row, blocked: `Your role can't edit “${decl.label}”.` };
    }
    return {
      ...row,
      bringBack: {
        write: { kind: "field", ...base, value: thenValue ?? "" },
        undo: { kind: "field", ...base, value: nowValue ?? "" },
        describe: thenValue ? `set “${decl.label}” back to ${summariseValue(decl.type, shown(decl, thenValue)).text}` : `clear “${decl.label}”`,
      },
    };
  });
}

/** An action point's own status row, when it carries one (a Simple one, or an itemised one with no items). */
function segmentStatusRows(
  section: VSection,
  nowSeg: VSegment,
  thenSeg: VSegment | undefined,
  detail: InstanceDetail,
  perms: ComparePermissions,
  label: string,
): CompareRow {
  const thenStatus = thenSeg?.status;
  const row = makeRow(`status:${section.key}:${nowSeg.key}`, label, statusCell(thenStatus), statusCell(nowSeg.status));
  if (!row.changed || !thenStatus || section.fedFrom) return row;
  const blocked = statusBlock(thenStatus, section.key, nowSeg.key, "", detail, perms);
  if (blocked) return { ...row, blocked };
  return {
    ...row,
    bringBack: {
      write: { kind: "segmentStatus", sectionKey: section.key, segmentKey: nowSeg.key, statusKey: thenStatus.key },
      undo: { kind: "segmentStatus", sectionKey: section.key, segmentKey: nowSeg.key, statusKey: nowSeg.statusKey ?? thenStatus.key },
      describe: `set the status to ${thenStatus.name}`,
    },
  };
}

/** An action point's tally or rolled-up band: worked out, never copied. */
function segmentRollupRow(nowSeg: VSegment, thenSeg: VSegment | undefined, label: string, id: string): CompareRow {
  const cell = (s: VSegment | undefined): CompareCell =>
    !s ? { text: "No value", empty: true }
    : s.displayStatus ? { text: `${s.displayStatus.name} · ${s.done} of ${s.total}`, colour: s.colour }
    : { text: `${s.done} of ${s.total} done`, colour: s.colour };
  return makeRow(id, label, cell(thenSeg), cell(nowSeg), { rollup: true });
}

/**
 * Every row the card shows at a level, changed ones first in their natural order, then unchanged ones —
 * the card collapses those under one disclosure.
 */
export function compareRows(
  where: CompareLevel,
  nowVm: DialVM,
  thenVm: DialVM,
  snapshot: InstanceSnapshot,
  perms: ComparePermissions,
  today: Date = new Date(),
): CompareRow[] {
  const detail = nowVm.detail;
  const thenSection = (key: string) => thenVm.sections.find((s) => s.key === key);
  const rows: CompareRow[] = [];

  if (where.level === "dial") {
    rows.push(makeRow("complete", "Complete",
      { text: `${Math.round(thenVm.completion * 100)}%` },
      { text: `${Math.round(nowVm.completion * 100)}%` }, { rollup: true }));
    for (const section of nowVm.sections) {
      const then = thenSection(section.key);
      rows.push(makeRow(`section:${section.key}`, sectionTitle(section),
        { text: then ? `${then.done} of ${then.total} done` : "No value", colour: then?.status?.colour, empty: !then },
        { text: `${section.done} of ${section.total} done`, colour: section.status?.colour }, { rollup: true }));
    }
    rows.push(...fieldRows(detail, snapshot, "Dial", "", "", "", perms));
  }

  if (where.level === "section") {
    const section = nowVm.sections.find((s) => s.key === where.sectionKey);
    if (!section) return [];
    const then = thenSection(section.key);
    if (detail.sectionStatusEnabled && snapshot.sectionStatuses != null) {
      const palette = sectionStatusPalette(detail);
      const find = (key: string | undefined) => (key ? palette.find((s) => s.key === key) : undefined);
      const thenKey = snapshot.sectionStatuses.find((s) => s.sectionKey === section.key)?.statusKey;
      const nowKey = detail.sectionStatuses?.find((s) => s.sectionKey === section.key)?.statusKey;
      const row = makeRow(`sectionStatus:${section.key}`, "Section status", statusCell(find(thenKey)), statusCell(find(nowKey)));
      if (row.changed && !section.fedFrom) {
        const target = find(thenKey);
        const blocked = target && !perms.canSet(target, section.key, "") ? `Your role can't set “${target.name}” here.` : null;
        rows.push(blocked ? { ...row, blocked } : {
          ...row,
          bringBack: {
            write: { kind: "sectionStatus", sectionKey: section.key, statusKey: thenKey ?? null },
            undo: { kind: "sectionStatus", sectionKey: section.key, statusKey: nowKey ?? null },
            describe: target ? `set the section status to ${target.name}` : "clear the section status",
          },
        });
      } else {
        rows.push(row);
      }
    }
    rows.push(makeRow(`sectionDone:${section.key}`, "Done",
      { text: then ? `${then.done} of ${then.total}` : "No value", empty: !then },
      { text: `${section.done} of ${section.total}` }, { rollup: true }));
    for (const seg of section.segments) {
      const thenSeg = then?.segments.find((s) => s.key === seg.key);
      rows.push(seg.itemActive || !seg.status
        ? segmentRollupRow(seg, thenSeg, seg.name, `segment:${section.key}:${seg.key}`)
        : segmentStatusRows(section, seg, thenSeg, detail, perms, seg.name));
    }
    rows.push(...fieldRows(detail, snapshot, "Section", section.key, "", "", perms));
  }

  if (where.level === "action") {
    const section = nowVm.sections.find((s) => s.key === where.sectionKey);
    const seg = section?.segments.find((s) => s.key === where.segmentKey);
    if (!section || !seg) return [];
    const thenSeg = thenSection(section.key)?.segments.find((s) => s.key === seg.key);

    if (!seg.itemActive && seg.status) {
      rows.push(segmentStatusRows(section, seg, thenSeg, detail, perms, "Status"));
      const thenDate = snapshot.statuses.find((s) => s.sectionKey === section.key && s.segmentKey === seg.key)?.reviewDate ?? null;
      const nowDate = seg.reviewDate ?? null;
      if (thenDate || nowDate) {
        const cell = (iso: string | null, withOverdue: boolean): CompareCell =>
          iso ? { text: fmtDate(iso)!, overdue: withOverdue ? overdueBy(iso, today) : undefined } : { text: "No date", empty: true };
        const row = makeRow(`review:${section.key}:${seg.key}`, "Review date (as recorded)", cell(thenDate, false), cell(nowDate, true));
        const blocked = seg.status && !perms.canSet(seg.status, section.key, seg.key) ? `Your role can't change this review date.` : null;
        rows.push(!row.changed || section.fedFrom || !seg.statusKey ? row
          : blocked ? { ...row, blocked }
          : {
            ...row,
            bringBack: {
              write: { kind: "reviewDate", sectionKey: section.key, segmentKey: seg.key, statusKey: seg.statusKey, reviewDate: thenDate, revertToStatusKey: seg.revertTo ?? null },
              undo: { kind: "reviewDate", sectionKey: section.key, segmentKey: seg.key, statusKey: seg.statusKey, reviewDate: nowDate, revertToStatusKey: seg.revertTo ?? null },
              describe: thenDate ? `set the review date to ${fmtDate(thenDate)}` : "clear the review date",
            },
          });
      }
    } else {
      rows.push(segmentRollupRow(seg, thenSeg, "Items done", `apDone:${section.key}:${seg.key}`));
    }

    rows.push(...fieldRows(detail, snapshot, "Segment", section.key, seg.key, "", perms));

    // Every item on every axis it's scored on, then its own fields.
    const itemStatus = (list: { sectionKey: string; segmentKey: string; itemKey: string; dimensionKey: string; statusKey: string }[], itemKey: string, dimKey: string) =>
      list.find((s) => s.sectionKey === section.key && s.segmentKey === seg.key && s.itemKey === itemKey && s.dimensionKey === dimKey)?.statusKey;
    for (const item of seg.items) {
      for (const dim of nowVm.dimensions) {
        const nowKey = itemStatus(detail.itemStatuses ?? [], item.key, dim.key);
        const thenKey = itemStatus(snapshot.itemStatuses, item.key, dim.key);
        if (!nowKey && !thenKey) continue;
        const find = (key: string | undefined) => (key ? dim.palette.statuses.find((s) => s.key === key) : undefined);
        const label = nowVm.dimensions.length > 1 ? `${item.name} · ${dim.label}` : item.name;
        const row = makeRow(`item:${section.key}:${seg.key}:${item.key}:${dim.key}`, label, statusCell(find(thenKey)), statusCell(find(nowKey)));
        const target = find(thenKey);
        if (!row.changed || !target || !nowKey || section.fedFrom) {
          rows.push(row);
          continue;
        }
        const blocked = statusBlock(target, section.key, seg.key, item.key, detail, perms);
        rows.push(blocked ? { ...row, blocked } : {
          ...row,
          bringBack: {
            write: { kind: "itemStatus", sectionKey: section.key, segmentKey: seg.key, itemKey: item.key, dimensionKey: dim.key, statusKey: target.key },
            undo: { kind: "itemStatus", sectionKey: section.key, segmentKey: seg.key, itemKey: item.key, dimensionKey: dim.key, statusKey: nowKey },
            describe: `set ${label} to ${target.name}`,
          },
        });
      }
      rows.push(...fieldRows(detail, snapshot, "Item", section.key, seg.key, item.key, perms, `${item.name} · `));
    }
  }

  return [...rows.filter((r) => r.changed), ...rows.filter((r) => !r.changed)];
}
